
import { Component, Vue } from "vue-property-decorator";
import {
  CashOrderApprovalStatusEnum,
  getOrderDetailStringField,
  IApprovalsFilterModel,
  ICashOrderDTO,
  IRequestedAmount,
  IRequestHistorialsService,
  ISolCustomerCommodityType,
  ISolCustomerCurrency,
  ISolCustomerPack,
  ISolCustomerSupply,
  IUserDetailData,
  normalizeOrderRequestedAmounts,
  normalizeOrderSupplies,
  resolveOrderDetailSource,
  SourceRequestViewEnum,
  resolveSolSubmissionMessage,
} from "@/entities";
import ApprovalsFilter from "@/components/approvals/ApprovalsFilter.vue";
import ApprovalsTable from "@/components/approvals/ApprovalsTable.vue";
import {
  IApprovalsUpdateStatusResponse,
  IApprovalsParams,
  ISolCustomerCurrencyParams,
  ISolCustomerPackParams,
  ISolCustomerPackResponse,
  ISolCustomerSupplyParams,
  ISolCustomerSupplyResponse,
} from "@/services";
import IconApprovals from "@/components/common/icon/IconApprovals.vue";
import DetailOrderModal from "@/components/orders/DetailOrderModal.vue";
import { Action, Getter } from "vuex-class";
import ApprovalsExportModal from "@/pages/approvals/ApprovalsExportModal.vue";
import RejectOrderRowModal from "@/components/approvals/RejectOrderRowModal.vue";

@Component({
  name: "approvals-page",
  components: {
    RejectOrderRowModal,
    IconApprovals,
    ApprovalsFilter,
    ApprovalsTable,
    DetailOrderModal,
    ApprovalsExportModal,
  },
})
export default class ApprovalsPage extends Vue {
  @Getter("getLoggedUser") getLoggedUser!: IUserDetailData;
  @Getter("getSolCustomerCurrencies") currencies!: ISolCustomerCurrency[];
  @Action("fetchSolCustomerCurrency") fetchSolCustomerCurrency!: (
    params: ISolCustomerCurrencyParams
  ) => Promise<void>;
  @Action("fetchSolCustomerPacks") fetchSolCustomerPacks!: (
    params: ISolCustomerPackParams
  ) => Promise<ISolCustomerPackResponse | undefined>;
  @Action("fetchSolCustomerSupply") fetchSolCustomerSupply!: (
    params: ISolCustomerSupplyParams
  ) => Promise<ISolCustomerSupplyResponse | undefined>;
  orders: ICashOrderDTO[] = [];
  approvalPacks: ISolCustomerPack[] = [];
  approvalCommodityTypes: ISolCustomerCommodityType[] = [];
  approvalCommodityTypesServicePointId: string | null = null;
  selectedOrder: ICashOrderDTO | null = null;
  approvalComments = "";
  isUpdatingApproval = false;

  filters: IApprovalsFilterModel = this.defaultFilters();
  openModalExport = false;
  orderToReject: ICashOrderDTO | null = null;

  async mounted(): Promise<void> {
    await this.fetchOrders();
    await this.setupCustomerCurrency();
    await this.setupCustomerPacks();
  }

  get pending(): ICashOrderDTO[] {
    return this.orders.filter((o) => o.approvalStatus === CashOrderApprovalStatusEnum.PENDING);
  }

  get approved(): ICashOrderDTO[] {
    return this.orders.filter((o) => o.approvalStatus === CashOrderApprovalStatusEnum.APPROVED);
  }

  get rejected(): ICashOrderDTO[] {
    return this.orders.filter((o) => o.approvalStatus === CashOrderApprovalStatusEnum.REJECTED);
  }

  get expired(): ICashOrderDTO[] {
    return this.orders.filter((o) => o.approvalStatus === CashOrderApprovalStatusEnum.EXPIRED);
  }

  get isPendingApprovalSelected(): boolean {
    return this.selectedOrder?.approvalStatus === CashOrderApprovalStatusEnum.PENDING || false;
  }

  get countryCode(): string {
    return this.getLoggedUser.country?.code || "";
  }

  async fetchOrders(): Promise<void> {
    const params: IApprovalsParams = {
      approvalStatus: this.filters.approvalStatus,
      username: this.filters.username,
      serviceCode: this.filters.serviceCode,
      orderServiceDateFrom: this.toIsoStartOfDay(this.filters.orderDateFrom),
      orderServiceDateTo: this.toIsoEndOfDay(this.filters.orderDateTo),
      sourceRequestView: SourceRequestViewEnum.APPROVALS,
    };

    const res = await this.$services.approvals.getOrders(params);
    this.orders = res || [];
  }

  async openOrderDetail(order: ICashOrderDTO): Promise<void> {
    await this.setupCommodityTypes(order);

    const detail = this.parseOrderDetail(order);
    if (!detail) {
      this.makeToast("warning", this.$t("message.approvals.detailReadError").toString());
      return;
    }

    this.selectedOrder = order;
    this.approvalComments = order.commentsUpdated || "";

    const detailOrderModal = this.$refs.detailOrderModal as DetailOrderModal;
    detailOrderModal.$emit("showDetailOrderModal", detail);
  }

  async onApprove(comments: string): Promise<void> {
    if (!this.selectedOrder) {
      return;
    }
    await this.approveOrder(this.selectedOrder, comments);
  }

  async onReject(comments: string): Promise<void> {
    if (!this.selectedOrder) {
      return;
    }
    await this.rejectOrder(this.selectedOrder, comments);
  }

  private async approveOrder(order: ICashOrderDTO, comments: string): Promise<void> {
    const normalizedComments = this.normalizeComments(comments);
    if (normalizedComments === null) {
      return;
    }

    const confirmed = await this.confirmApprovalAction("approve");
    if (!confirmed) {
      return;
    }

    await this.updateApprovalStatus(
      order,
      CashOrderApprovalStatusEnum.APPROVED,
      normalizedComments || undefined
    );
  }

  private async rejectOrder(order: ICashOrderDTO, comments: string): Promise<void> {
    const normalizedComments = this.normalizeComments(comments);
    if (normalizedComments === null) {
      return;
    }

    if (!normalizedComments) {
      this.makeToast("warning", this.$t("message.approvals.rejectReasonRequired").toString());
      return;
    }

    const confirmed = await this.confirmApprovalAction("reject");
    if (!confirmed) {
      return;
    }

    await this.updateApprovalStatus(
      order,
      CashOrderApprovalStatusEnum.REJECTED,
      normalizedComments
    );
  }

  private async updateApprovalStatus(
    order: ICashOrderDTO,
    status: CashOrderApprovalStatusEnum.APPROVED | CashOrderApprovalStatusEnum.REJECTED,
    comments?: string
  ): Promise<void> {
    const countryCode = this.getLoggedUser.country?.code;
    if (!countryCode) {
      this.makeToast("warning", this.$t("message.approvals.countryMissing").toString());
      return;
    }

    this.isUpdatingApproval = true;
    const result = await this.$services.approvals.updateOrderStatus({
      countryCode,
      orderId: order.id,
      approvalStatus: status,
      comments,
    });
    this.isUpdatingApproval = false;

    const toastVariant = result.success ? "success" : "danger";
    this.makeToast(toastVariant, this.resolveApprovalStatusMessage(result, status));
    await this.fetchOrders();
    this.$bvModal.hide("order-detail-modal");
    this.selectedOrder = null;
    this.approvalComments = "";
  }

  async onApproveFromRow(order: ICashOrderDTO): Promise<void> {
    await this.approveOrder(order, "");
  }

  onRejectFromRow(order: ICashOrderDTO): void {
    this.orderToReject = order;
    this.$nextTick(() => {
      this.$bvModal.show("reject-order-row-modal");
    });
  }

  async onConfirmRejectFromRow(comments: string): Promise<void> {
    if (!this.orderToReject) {
      return;
    }
    this.$bvModal.hide("reject-order-row-modal");
    await this.rejectOrder(this.orderToReject, comments);
    this.orderToReject = null;
  }

  private async confirmApprovalAction(action: "approve" | "reject"): Promise<boolean> {
    const message =
      action === "approve"
        ? this.$t("message.approvals.confirmApprove").toString()
        : this.$t("message.approvals.confirmReject").toString();

    const { isConfirmed } = await this.$swal({
      title: this.$t("message.approvals.confirmTitle").toString(),
      text: message,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: this.$t("label.yes").toString(),
      cancelButtonText: this.$t("label.no").toString(),
    });

    return Boolean(isConfirmed);
  }

  private parseOrderDetail(order: ICashOrderDTO): IRequestHistorialsService | null {
    try {
      const parsed = JSON.parse(order.message);
      const detailSource = this.resolveDetailSource(parsed) ?? {};
      const productCode = detailSource?.productCode || order.productCode;
      const serviceType = this.normalizeServiceType(detailSource?.serviceType);
      const hasOrderTypeContext = Boolean(productCode) || typeof serviceType === "number";
      if (!hasOrderTypeContext) {
        return null;
      }
      const servicePoint = order.servicePoint;
      const subclient = servicePoint?.subclient;
      const client = subclient?.client;

      const createdAt =
        typeof order.createdAt === "string"
          ? order.createdAt
          : new Date(order.createdAt).toISOString();

      return {
        ...detailSource,
        serviceType,
        productCode,
        serviceCode: detailSource?.serviceCode || order.serviceCode,
        requestedAmounts: this.normalizeRequestedAmounts(detailSource?.requestedAmounts),
        supplies: this.normalizeSupplies(
          (detailSource as Record<string, unknown> | null)?.supplies
        ),
        faultDescription: this.getStringField(detailSource, "faultDescription"),
        assistanceObservation:
          this.getStringField(detailSource, "assistanceObservation") ||
          this.getStringField(detailSource, "comments"),
        requestedTime: detailSource?.requestedTime || createdAt,
        createdAt: detailSource?.createdAt || createdAt,
        applicant:
          this.getStringField(detailSource, "applicant") ||
          this.getStringField(detailSource, "userName") ||
          order.user?.username,
        entityDescription:
          this.getStringField(detailSource, "entityDescription") ||
          this.formatCodeDescription(client?.codeSol, client?.description),
        centerDescription:
          this.getStringField(detailSource, "centerDescription") ||
          this.formatCodeDescription(subclient?.codeSol, subclient?.description),
        servicePointDescription:
          this.getStringField(detailSource, "servicePointDescription") ||
          this.formatCodeDescription(servicePoint?.codeSol, servicePoint?.description),
      } as IRequestHistorialsService;
    } catch {
      return null;
    }
  }

  private resolveDetailSource(parsed: unknown): Partial<IRequestHistorialsService> | null {
    return resolveOrderDetailSource(parsed);
  }

  private formatCodeDescription(code?: string, description?: string): string {
    if (code && description) {
      return `${code} - ${description}`;
    }
    return description || code || "";
  }

  private getStringField(source: unknown, key: string): string | undefined {
    return getOrderDetailStringField(source, key);
  }

  private normalizeRequestedAmounts(value: unknown): IRequestedAmount[] | undefined {
    return normalizeOrderRequestedAmounts(value, {
      packs: this.approvalPacks,
      currencies: this.currencies,
    });
  }

  private normalizeSupplies(value: unknown): ISolCustomerSupply[] | undefined {
    return normalizeOrderSupplies(value, this.approvalCommodityTypes);
  }

  private normalizeServiceType(value: unknown): number | undefined {
    if (typeof value === "number" && Number.isInteger(value)) {
      return value;
    }

    if (typeof value === "string" && value.trim() !== "") {
      const parsedValue = Number(value);
      return Number.isInteger(parsedValue) ? parsedValue : undefined;
    }

    return undefined;
  }

  private async setupCustomerCurrency(): Promise<void> {
    if (!this.countryCode) {
      return;
    }

    await this.fetchSolCustomerCurrency({ countryCode: this.countryCode });
  }

  private async setupCustomerPacks(): Promise<void> {
    const params = {
      countryCode: this.countryCode,
    } as ISolCustomerPackParams;

    this.approvalPacks = (await this.fetchSolCustomerPacks(params))?.packs || [];
  }

  private async setupCommodityTypes(order: ICashOrderDTO): Promise<void> {
    const servicePointId = order.servicePoint?.oidSol;
    if (!this.countryCode || !servicePointId) {
      this.approvalCommodityTypes = [];
      this.approvalCommodityTypesServicePointId = null;
      return;
    }

    if (this.approvalCommodityTypesServicePointId === servicePointId) {
      return;
    }

    const response = await this.fetchSolCustomerSupply({
      countryCode: this.countryCode,
      servicePointId,
    });

    if (!response) {
      this.approvalCommodityTypes = [];
      this.approvalCommodityTypesServicePointId = null;
      return;
    }

    this.approvalCommodityTypes = response.commodityTypes || [];
    this.approvalCommodityTypesServicePointId = servicePointId;
  }

  private makeToast(variant: string, message: string): void {
    this.$bvToast.toast(message, {
      autoHideDelay: 4000,
      variant,
      toaster: "b-toaster-bottom-center",
      noCloseButton: true,
    });
  }

  private normalizeComments(comments: string): string | null {
    const trimmedComments = comments?.trim() || "";
    if (trimmedComments.length > 400) {
      this.makeToast("warning", this.$t("message.approvals.commentMaxLength").toString());
      return null;
    }
    return trimmedComments;
  }

  private resolveApprovalStatusMessage(
    result: IApprovalsUpdateStatusResponse,
    status: CashOrderApprovalStatusEnum.APPROVED | CashOrderApprovalStatusEnum.REJECTED
  ): string {
    const successFallbackKey =
      status === CashOrderApprovalStatusEnum.APPROVED
        ? "message.order.send.success"
        : "message.approvals.updateSuccess";

    return resolveSolSubmissionMessage({
      result,
      te: (key) => (typeof this.$te === "function" ? this.$te(key) : false),
      t: (key) => this.$t(key).toString(),
      successFallbackKey,
      failureFallbackKey: "message.order.send.failed.create",
    });
  }
  private defaultFilters(): IApprovalsFilterModel {
    const today = new Date();
    today.setDate(today.getDate() + 7);
    today.setHours(0, 0, 0, 0);

    const from = new Date(today);
    from.setDate(from.getDate() - 7);

    return {
      approvalStatus: [],
      username: undefined,
      serviceCode: undefined,
      orderDateFrom: from,
      orderDateTo: today,
    };
  }

  private toIsoStartOfDay(d: Date): string {
    const tmp = new Date(d);
    tmp.setHours(0, 0, 0, 0);
    return tmp.toISOString();
  }

  private toIsoEndOfDay(d: Date): string {
    const tmp = new Date(d);
    tmp.setHours(23, 59, 59, 999);
    return tmp.toISOString();
  }

  openModal(): void {
    this.openModalExport = true;
    this.$nextTick(() => {
      this.$bvModal.show("approvalsExportModal");
    });
  }

  closeModal({ isNotified }: { isNotified: boolean }): void {
    this.$bvModal.hide("approvalsExportModal");
    this.openModalExport = false;
    if (isNotified) {
      this.makeToast("success", this.$t("message.approvals.export.ok").toString());
    }
  }
}
